import { Link } from 'react-router-dom'
import { ROUTE_BUILDERS } from '@/config/routes'
import { Pencil } from 'lucide-react'
import { useUserRole } from '@/hooks/useUserRole'
import { AccessConfirmationIconButton } from '@/components/ui/AccessConfirmationIconButton'
import { StatementListDownloadIconButton } from '@/components/ui/StatementListIcon'
import { LIBERTY_ROLES } from '../constants'
// import { DocumentStatusInfoIcon } from './DocumentStatusInfoIcon'
import { formatCurrency, formatDate, formatStatusLabel, formatYear } from '../utils/formatters'
import {
  displayDocumentStatus,
  padApplicantId,
} from '../utils/statusMaps'
import { reimbursementTypeLabel, reimbursementTypeTextClass } from '@/features/resident/utils/residentListStatusStyles'
import type { ReimbursementsGridProps } from '../types'
import {
  getReimbursementListProgressPillClassName,
  getReimbursementWorkflowStatusPillClassName,
  resolveReimbursementListProgressLabel,
} from '../utils/reimbursementHeaderSummaryFields'

export function ReimbursementsGrid({
  items,
  onAccessConfirmation,
  onStatementDownload,
  processingMap,
  statusLabelMaps,
}: ReimbursementsGridProps) {
  const { roleName, orgId, hasPolicy } = useUserRole()
  const isLibertyRole = roleName
    ? LIBERTY_ROLES.includes(roleName as (typeof LIBERTY_ROLES)[number])
    : false
  const isLibertyOrg = Number(orgId) === 1
  const showProperty = hasPolicy(['default_list_property_view'])
  const showCompany = hasPolicy(['default_list_company_view'])
  const canViewDetail = hasPolicy(['default_detail_view'])
  const canViewCharityDetail = hasPolicy(['charity_default_detail_view'])

  return (
    <div
      className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3"
      data-testid="reimbursements-grid"
    >
      {items.map((item, index) => {
        const progress = String(item.reimbursement_rental_progress ?? '').trim()
        const showStatement =
          !!item.default_statement &&
          ['COMPLETED', 'PAID'].includes(progress.toUpperCase())

        return (
          <div
            key={item.reimbursement_id ?? `${item.applicant_id}-${index}`}
            className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm space-y-3"
            data-testid={`reimbursements-grid-card-${item.reimbursement_id ?? item.applicant_id}`}
          >
            <div className="space-y-2 text-xs">
              {isLibertyOrg && (
                <div className="flex justify-between items-center">
                  <span className="font-medium text-slate-400 uppercase tracking-wider">Applicant</span>
                  <span className="font-mono text-slate-700 font-semibold">
                    {canViewDetail ? (
                      <Link
                        to={`${ROUTE_BUILDERS.DEFAULT_DETAIL(
                          item.applicant_id,
                          item.fk_user_id,
                          item.reimbursement_id,
                        )}#default`}
                        className="text-[rgb(21_151_255)] hover:text-[#0d7acc] hover:underline"
                      >
                        {padApplicantId(item.applicant_id)}
                      </Link>
                    ) : (
                      padApplicantId(item.applicant_id)
                    )}
                  </span>
                </div>
              )}

              <div className="flex justify-between items-center">
                <span className="font-medium text-slate-400 uppercase tracking-wider">Resident</span>
                <span className="font-semibold text-slate-900">
                  {!isLibertyOrg && canViewCharityDetail && item.resident_id ? (
                    <a
                      href={`/charity-defaults/${item.resident_id}`}
                      className="text-[rgb(21_151_255)] hover:underline"
                    >
                      {item.resident_name}
                    </a>
                  ) : (
                    item.resident_name
                  )}
                </span>
              </div>

              {showProperty && (
                <div className="flex justify-between items-center">
                  <span className="font-medium text-slate-400 uppercase tracking-wider">Property</span>
                  <span className="text-slate-700">{item.property_name ?? '—'}</span>
                </div>
              )}

              {showCompany && (
                <div className="flex justify-between items-center">
                  <span className="font-medium text-slate-400 uppercase tracking-wider">Management Company</span>
                  <span className="text-slate-700">{item.company_name ?? '—'}</span>
                </div>
              )}

              <div className="flex justify-between items-center">
                <span className="font-medium text-slate-400 uppercase tracking-wider">Agent Responsible</span>
                <span className="text-slate-700">{item.agent_name ?? '—'}</span>
              </div>

              <div className="flex justify-between items-center">
                <span className="font-medium text-slate-400 uppercase tracking-wider">Access Fee Paid</span>
                <span className="text-slate-700 tabular-nums">
                  {item.certificate_amount ? formatCurrency(item.certificate_amount) : ''}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className="font-medium text-slate-400 uppercase tracking-wider">Reimbursement Paid</span>
                <span className="text-slate-700 tabular-nums">
                  {item.default_paid ? formatCurrency(item.default_paid) : ''}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className="font-medium text-slate-400 uppercase tracking-wider">Reimbursement Type</span>
                <span className={reimbursementTypeTextClass(item.reimbursement_type)}>
                  {reimbursementTypeLabel(item.reimbursement_type, statusLabelMaps) || '—'}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className="font-medium text-slate-400 uppercase tracking-wider">Reimbursement Status</span>
                {item.reimbursement_rental_status ? (
                  <span className={getReimbursementWorkflowStatusPillClassName((item.reimbursement_rental_status))}>
                    {formatStatusLabel(item.reimbursement_rental_status)}
                  </span>
                ) : (
                  <span className="text-slate-700">—</span>
                )}
              </div>

              <div className="flex justify-between items-center">
                <span className="font-medium text-slate-400 uppercase tracking-wider">Reimbursement Progress</span>
                <span>
                  {item.reimbursement_rental_progress ? (
                    <span
                      className={getReimbursementListProgressPillClassName(
                        item.reimbursement_rental_progress,
                      )}
                    >
                      {resolveReimbursementListProgressLabel(
                        item.reimbursement_rental_progress,
                        statusLabelMaps?.reimbursement_rental_progress,
                      ) ?? item.reimbursement_rental_progress}
                    </span>
                  ) : (
                    <span className="text-slate-700">—</span>
                  )}
                </span>
              </div>

              {isLibertyRole && (
                <div className="flex justify-between items-center">
                  <span className="font-medium text-slate-400 uppercase tracking-wider">Review Decision</span>
                  <span className="text-slate-700">{item.review_decision ?? '—'}</span>
                </div>
              )}

              <div className="flex justify-between items-center">
                <span className="font-medium text-slate-400 uppercase tracking-wider">Document Status</span>
                <div className="inline-flex items-center gap-1 text-slate-700">
                  <span>{displayDocumentStatus(item.document_status ?? '') || '—'}</span>
                  {/* <DocumentStatusInfoIcon item={item} /> */}
                </div>
              </div>

              {isLibertyOrg && (
                <div className="flex justify-between items-center">
                  <span className="font-medium text-slate-400 uppercase tracking-wider">Lease Start Date</span>
                  <span className="text-slate-700">{formatDate(item.contract_start_date) || '—'}</span>
                </div>
              )}

              <div className="flex justify-between items-center">
                <span className="font-medium text-slate-400 uppercase tracking-wider">Vintage Year</span>
                <span className="text-slate-700">{formatYear(item.contract_start_date) || '—'}</span>
              </div>

              <div className="flex justify-between items-center">
                <span className="font-medium text-slate-400 uppercase tracking-wider">Reimbursement Request Date</span>
                <span className="text-slate-700">{formatDate(item.rental_status_date) || '—'}</span>
              </div>

              <div className="flex justify-between items-center">
                <span className="font-medium text-slate-400 uppercase tracking-wider">Reimbursement Status Date</span>
                <span className="text-slate-700">{formatDate(item.default_status_date) || '—'}</span>
              </div>
            </div>

            <div className="pt-2 border-t border-slate-100 flex items-center justify-between">
              <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Action</span>
              <div className="flex items-center gap-2">
                {isLibertyOrg ? (
                  canViewDetail && (
                    <Link
                      to={`${ROUTE_BUILDERS.DEFAULT_DETAIL(
                        item.applicant_id,
                        item.fk_user_id,
                        item.reimbursement_id,
                      )}#default`}
                      className="p-1 text-slate-500 hover:text-[rgb(21_151_255)] hover:bg-slate-100 rounded-lg transition-colors"
                      title="Edit"
                      data-testid={`reimbursements-grid-edit-${item.applicant_id}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </Link>
                  )
                ) : (
                  canViewCharityDetail && item.resident_id && (
                    <a
                      href={`/charity-defaults/${item.resident_id}`}
                      className="p-1 text-slate-500 hover:text-[rgb(21_151_255)] hover:bg-slate-100 rounded-lg transition-colors"
                      title="Edit"
                      data-testid={`reimbursements-grid-charity-edit-${item.resident_id}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </a>
                  )
                )}

                {showStatement && (
                  <StatementListDownloadIconButton
                    onClick={() => onStatementDownload?.(item)}
                    aria-label={
                      item.default_statement
                        ? `Open statement: ${item.default_statement}`
                        : 'Open statement'
                    }
                    dataTestId={`reimbursements-grid-statement-download-${item.applicant_id}`}
                  />
                )}

                {isLibertyOrg && onAccessConfirmation && (
                  <AccessConfirmationIconButton
                    onClick={() => onAccessConfirmation(item)}
                    busy={!!processingMap?.[`policyDecl-${item.application_id ?? item.applicant_id}`]}
                    title="Click to Download"
                    dataTestId={`reimbursements-grid-access-confirmation-${item.applicant_id}`}
                  />
                )}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
